// Event times come from the API as an absolute UTC instant plus an IANA
// `timezone` the organizer authored them in. We render them in that fixed zone
// (not the viewer's) so a "2:00 PM Shanghai" event reads the same everywhere,
// with the zone abbreviation appended for clarity. When `timezone` is missing
// (legacy events) we fall back to the viewer's local zone — the old behavior.

function withZone(tz?: string): { timeZone?: string } {
  return tz ? { timeZone: tz } : {}
}

// Short zone label for a given instant, e.g. "CST", "PDT", "GMT+8".
function zoneLabel(d: Date, tz?: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      ...withZone(tz),
      hour: 'numeric',
      timeZoneName: 'short',
    }).formatToParts(d)
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? ''
  } catch {
    return ''
  }
}

// Date + time range rendered in the event's timezone with a zone label.
// `dateOptions` controls the date portion (e.g. include weekday on detail pages).
export function formatEventRange(
  startIso: string | undefined,
  endIso: string | undefined,
  tz: string | undefined,
  dateOptions: Intl.DateTimeFormatOptions,
): string {
  if (!startIso) return ''
  const start = new Date(startIso)
  if (Number.isNaN(start.getTime())) return ''

  const dateStr = start.toLocaleDateString('en-US', { ...dateOptions, ...withZone(tz) })
  const timeOpts: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', ...withZone(tz) }
  const startTime = start.toLocaleTimeString('en-US', timeOpts)
  const zone = zoneLabel(start, tz)
  const suffix = zone ? ` ${zone}` : ''

  if (endIso) {
    const end = new Date(endIso)
    if (!Number.isNaN(end.getTime())) {
      const endTime = end.toLocaleTimeString('en-US', timeOpts)
      return `${dateStr} · ${startTime} – ${endTime}${suffix}`
    }
  }
  return `${dateStr} · ${startTime}${suffix}`
}

// Date only, rendered in the event's timezone (no zone label).
export function formatEventDate(
  iso: string | undefined,
  tz: string | undefined,
  options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' },
): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', { ...options, ...withZone(tz) })
}
